import {
  isAirUnit,
  isFootUnit,
  UNIT_KINDS,
  type FacingDir,
  type PlayerId,
  type RiflemanSkin,
  type UnitKind,
} from "@dailywars/core/engine";

import { loadCachedImage } from "#/game/cachedImage";
import { isImageReady } from "#/game/imageReady";

export interface SpriteUnitFrame {
  img: HTMLImageElement;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  flipX: boolean;
}

let activeRiflemanSkin: RiflemanSkin | undefined;

export function setActiveRiflemanSkin(skin: RiflemanSkin | undefined): void {
  activeRiflemanSkin = skin;
}

function riflemanVariantPrefix(kind: UnitKind, owner: PlayerId): string {
  if (kind !== "rifleman" || owner !== "p1" || !activeRiflemanSkin) return "";
  return `${activeRiflemanSkin}-`;
}

function iconUrl(kind: UnitKind, owner: PlayerId): string {
  return `/units/${kind}/icon-${riflemanVariantPrefix(kind, owner)}${owner}.png`;
}

function movedIconUrl(kind: UnitKind, owner: PlayerId): string {
  return `/units/${kind}/icon-moved-${riflemanVariantPrefix(kind, owner)}${owner}.png`;
}

function shadowUrl(kind: UnitKind): string {
  return `/units/${kind}/shadow.png`;
}

export function hasUnitShadow(kind: UnitKind): boolean {
  return isFootUnit(kind) || isAirUnit(kind);
}

export function getSpriteUnitIconSrc(kind: UnitKind, owner: PlayerId): string {
  return iconUrl(kind, owner);
}

export function getSpriteUnitIconImage(kind: UnitKind, owner: PlayerId): HTMLImageElement {
  return loadCachedImage(iconUrl(kind, owner));
}

export function getSpriteUnitMovedIconSrc(kind: UnitKind, owner: PlayerId): string {
  return movedIconUrl(kind, owner);
}

export function getSpriteUnitShadowSrc(kind: UnitKind): string {
  return shadowUrl(kind);
}

export function getSpriteUnitShadowImage(kind: UnitKind): HTMLImageElement {
  return loadCachedImage(shadowUrl(kind));
}

export function preloadUnitSprites(): HTMLImageElement[] {
  const images: HTMLImageElement[] = [];
  for (const kind of UNIT_KINDS) {
    for (const owner of ["p1", "p2"] as const) {
      images.push(getSpriteUnitIconImage(kind, owner));
      images.push(loadCachedImage(movedIconUrl(kind, owner)));
      if (hasUnitShadow(kind)) {
        images.push(getSpriteUnitShadowImage(kind));
      }
    }
  }
  return images;
}

export function getSpriteUnitFrame({
  kind,
  owner,
  facing,
  moved = false,
}: {
  kind: UnitKind;
  owner: PlayerId;
  facing: FacingDir;
  moved?: boolean;
}): SpriteUnitFrame | null {
  const img = moved
    ? loadCachedImage(movedIconUrl(kind, owner))
    : getSpriteUnitIconImage(kind, owner);
  if (!isImageReady(img)) return null;
  return {
    img,
    sx: 0,
    sy: 0,
    sw: img.naturalWidth,
    sh: img.naturalHeight,
    flipX: facing === "left",
  };
}

const frameSilhouetteCache = new Map<string, HTMLCanvasElement>();

export function getSpriteUnitFrameSilhouette(frame: SpriteUnitFrame): HTMLCanvasElement | null {
  const key = `${frame.img.src}:${frame.sx}:${frame.sy}:${frame.sw}:${frame.sh}`;
  const cached = frameSilhouetteCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = frame.sw;
  canvas.height = frame.sh;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(frame.img, frame.sx, frame.sy, frame.sw, frame.sh, 0, 0, frame.sw, frame.sh);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, frame.sw, frame.sh);
  frameSilhouetteCache.set(key, canvas);
  return canvas;
}
